<!--
  @component Navbar.MenuSubmenu

  A submenu component for the Navbar, typically used within Navbar.MenuItem to create nested navigation structures.
  It handles the rendering of child menu items and manages its open/closed state.

  @prop label - The accessible label for the submenu (required)
  @prop css - Custom CSS styles to apply to the submenu

  Example:
  <Navbar.MenuItem href="/products" label="Products">
    <Navbar.MenuSubmenu label="Product Categories">
      <Navbar.MenuItem href="/electronics" label="Electronics" />
      <Navbar.MenuItem href="/clothing" label="Clothing" />
    </Navbar.MenuSubmenu>
  </Navbar.MenuItem>
-->

<script lang="ts">
  import { getContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import { submenuStyles } from '../navbar.styles';
  import type { NavbarStore } from '../navbar-store.svelte';
  import { slide } from 'svelte/transition';
  import type { NavbarMenuItemStore, NavbarMenuSubmenuProps } from '../navbar.types';

  /** Props for the Navbar.MenuSubmenu component. */
  const { children, label }: NavbarMenuSubmenuProps = $props();

  /** The navbar store from context. */
  const navbarStore = getContext<NavbarStore>('navbar');
  /** The parent menu item store from context. */
  const navbarMenuItemStore = getContext<NavbarMenuItemStore>('navbar-menu-item');
  /** Styles for the submenu, derived from the depth of nesting. */
  const styles = $derived(submenuStyles.raw({ depth: navbarMenuItemStore.depth as 1 | 2 | 3 | 4 }));

  /** Whether the submenu is currently open. */
  const isOpen = $derived(navbarStore.openMenuItemId?.includes(navbarMenuItemStore.id));
</script>

{#if isOpen && children}
  <div class={css(styles)} role="menubar" id={`submenu-${navbarMenuItemStore.id}`} transition:slide={{ duration: 300 }}>
    {@render children()}
  </div>
{/if}
